import dayjs, { type Dayjs } from 'dayjs';
import type { DateValue } from '@/kit/DateField';
import { isUnincorporated } from '@/features/tr-reportsettings/clientKind';

/**
 * Adds months following UK statutory month-end rules (Companies Act 2006 s.442 / FA 1998 Sch 18).
 * If the starting date is the end of a month (e.g. 30 June, 30 September, 31 March, 28 February),
 * the target date will also be the end of that target month (e.g. 31 March, 30 June, 31 December, 30 November).
 */
export function addMonthsStatutory(date: Dayjs, months: number): Dayjs {
  const isEndOfMonth = date.date() === date.daysInMonth();
  const target = date.add(months, 'month');
  return isEndOfMonth ? target.endOf('month') : target;
}

export interface StatutoryDueDateOptions {
  periodFrom: DateValue;
  periodTo: DateValue;
  clientType?: number | null;
  companyType?: string | null;
  businessStartDate?: string | null;
  isFirstPeriod?: boolean;
}

export interface CalculatedDueDates {
  accountsDueDate: Dayjs | null;
  ct600DueDate: Dayjs | null;
}

/**
 * Recalculates Accounts Due Date and CT600 Due Date for an accounting period according to UK rules:
 * - Accounts Due Date:
 *   - For private companies and LLPs: period end + 9 months (Companies Act 2006 s.442(2)(a)).
 *   - For public companies (PLC): period end + 6 months (Companies Act 2006 s.442(2)(b)).
 *   - For first accounts: 21 months from incorporation for private companies (18 months for PLC)
 *     or 3 months from period end, whichever is later (Companies Act 2006 s.442(3)).
 * - CT600 Due Date:
 *   - Period end + 12 months (Finance Act 1998 Schedule 18 paragraph 14).
 * - For unincorporated clients (Sole Trader / Partnership):
 *   - Both dates are null (no Companies House or CT600 filing requirement).
 */
export function calculateStatutoryDueDates({
  periodFrom,
  periodTo,
  clientType,
  companyType,
  businessStartDate,
  isFirstPeriod,
}: StatutoryDueDateOptions): CalculatedDueDates {
  if (isUnincorporated(clientType)) {
    return { accountsDueDate: null, ct600DueDate: null };
  }

  const to = periodTo ? dayjs(periodTo) : null;
  if (!to || !to.isValid()) {
    return { accountsDueDate: null, ct600DueDate: null };
  }

  const isPlc = (companyType ?? '').toLowerCase() === 'plc';
  const from = periodFrom ? dayjs(periodFrom) : null;

  const isFirst =
    !!isFirstPeriod ||
    (!!businessStartDate && !!from && from.isValid() && from.isSame(dayjs(businessStartDate), 'day'));

  let accountsDueDate: Dayjs;
  if (isFirst) {
    const incDate = businessStartDate ? dayjs(businessStartDate) : from;
    const incDue = incDate && incDate.isValid() ? addMonthsStatutory(incDate, isPlc ? 18 : 21) : null;
    const endDue = addMonthsStatutory(to, 3);
    accountsDueDate = incDue && incDue.isAfter(endDue) ? incDue : endDue;
  } else {
    accountsDueDate = addMonthsStatutory(to, isPlc ? 6 : 9);
  }

  const ct600DueDate = addMonthsStatutory(to, 12);

  return { accountsDueDate, ct600DueDate };
}
